import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, FileArrowUp, Trash, DownloadSimple } from "@phosphor-icons/react";

const EMPTY = {
  name: "",
  employee_code: "",
  branch_id: "",
  designation: "",
  work_area: "",
  shift_start: "",
  shift_end: "",
  status: "active",
  date_of_joining: "",
  date_of_leaving: "",
  phone: "",
  email: "",
  address: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
  emergency_contact_relation: "",
  aadhar_no: "",
  pan_no: "",
  bank_account: "",
  ifsc_code: "",
  bank_name: "",
  gross_salary: 0,
  pf_applicable: false,
  esi_applicable: false,
  esi_pf_branch: "",
  holiday_pay_bonus: false,
  salary_credit_day: 7,
  mode_of_pay: "bank",
  source_paying_account: "228",
  has_salary_advance: false,
  advance_total_amount: 0,
  advance_installments: 0,
  advance_start_month: "",
  advance_recovery_amount: 0,
  working_days_per_month: 25,
  working_hours_per_month: 200,
  notes: "",
};

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={testId ? `${testId}-wrap` : undefined}>
      <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function DocsTab({ employeeId, docs, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [kind, setKind] = useState("aadhar");

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File exceeds 5 MB"); return; }
    setUploading(true);
    try {
      const reader = new FileReader();
      const data_b64 = await new Promise((res, rej) => {
        reader.onload = () => res(reader.result.split(",")[1] || "");
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      await api.post(`/hr/employees/${employeeId}/documents`, {
        name: file.name,
        kind,
        mime: file.type || "application/octet-stream",
        data_b64,
      });
      toast.success("Document uploaded");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const download = (d) => {
    const link = document.createElement("a");
    link.href = `data:${d.mime || "application/octet-stream"};base64,${d.data_b64}`;
    link.download = d.name;
    link.click();
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete document '${d.name}'?`)) return;
    try {
      await api.delete(`/hr/employees/${employeeId}/documents/${d.id}`);
      toast.success("Document removed");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="hr-emp-docs-tab">
      <div className="border border-canvas-line p-3 bg-canvas-panel/30">
        <div className="text-xs font-mono uppercase tracking-wider text-ink-muted mb-2">Upload new</div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            data-testid="hr-doc-kind"
            className="px-2 py-1.5 bg-canvas border border-canvas-line text-xs font-mono uppercase tracking-wider"
          >
            <option value="aadhar">Aadhar</option>
            <option value="pan">PAN</option>
            <option value="bank_passbook">Bank Passbook</option>
            <option value="offer_letter">Offer Letter</option>
            <option value="id_card">ID Card</option>
            <option value="other">Other</option>
          </select>
          <label className="px-3 py-1.5 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink cursor-pointer inline-flex items-center gap-1.5" data-testid="hr-doc-upload">
            <FileArrowUp size={12} weight="bold" />
            {uploading ? "Uploading…" : "Choose file"}
            <input
              type="file"
              onChange={(e) => upload(e.target.files?.[0])}
              disabled={uploading}
              className="hidden"
              accept="image/*,.pdf"
            />
          </label>
          <span className="text-[10px] text-ink-muted font-mono">Max 5 MB · Images or PDF</span>
        </div>
      </div>

      {docs.length === 0 && (
        <div className="text-xs text-ink-muted italic p-3 border border-dashed border-canvas-line text-center">No documents attached yet.</div>
      )}
      <div className="space-y-1">
        {docs.map((d) => (
          <div key={d.id} className="flex items-center justify-between border border-canvas-line px-3 py-2 hover:bg-canvas-panel/40" data-testid={`hr-doc-row-${d.id}`}>
            <div className="min-w-0">
              <div className="text-sm text-ink truncate">{d.name}</div>
              <div className="font-mono text-[10px] text-ink-muted">
                {(d.kind || "other").replace("_", " ").toUpperCase()} · uploaded {(d.uploaded_at || "").slice(0, 10)}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => download(d)} className="p-1.5 border border-canvas-line hover:border-ink" title="Download">
                <DownloadSimple size={12} weight="bold" />
              </button>
              <button onClick={() => remove(d)} className="p-1.5 border border-canvas-line hover:border-destructive hover:text-destructive" title="Delete">
                <Trash size={12} weight="bold" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function HrEmployeeFormDrawer({ open, editing, onClose, onSaved, canViewSalary = true }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("basic");
  const [docs, setDocs] = useState([]);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    if (!open) return;
    setTab("basic");
    api.get("/branches").then(({ data }) => setBranches(data || [])).catch(() => setBranches([]));
    if (editing) {
      setForm({ ...EMPTY, ...editing });
      setDocs(editing.documents || []);
    } else {
      setForm(EMPTY);
      setDocs([]);
    }
  }, [open, editing]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const refetch = async () => {
    if (!editing?.id) return;
    try {
      const { data } = await api.get(`/hr/employees/${editing.id}`);
      setDocs(data.documents || []);
    } catch { /* noop */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.employee_code.trim()) {
      toast.error("Name and Employee Code are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Convert blank date strings to null (Pydantic Optional[str] friendly).
      ["date_of_joining", "date_of_leaving"].forEach((k) => {
        if (!payload[k]) payload[k] = null;
      });
      // Non-salary-privileged users never see the Bank + Payroll tab — strip
      // those fields from the payload so an innocent save doesn't trip the
      // server's salary-write guard (or overwrite real values with blanks).
      if (!canViewSalary) {
        [
          "gross_salary", "salary_credit_day", "pf_applicable", "esi_applicable",
          "esi_pf_branch", "bank_account", "ifsc_code", "bank_name",
          "mode_of_pay", "source_paying_account", "has_salary_advance",
          "advance_total_amount", "advance_installments", "advance_start_month",
          "advance_recovery_amount",
        ].forEach((k) => delete payload[k]);
      }
      if (editing) {
        await api.put(`/hr/employees/${editing.id}`, payload);
        toast.success("Employee updated");
      } else {
        await api.post("/hr/employees", payload);
        toast.success("Employee added");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex justify-end" data-testid="hr-emp-form-drawer">
      <div className="bg-canvas w-full max-w-3xl h-full overflow-y-auto border-l border-canvas-line">
        <div className="sticky top-0 bg-canvas border-b border-canvas-line px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">
              {editing ? "Edit Employee" : "Add Employee"}
            </div>
            <div className="text-lg font-semibold">{editing?.name || "New employee"}</div>
          </div>
          <button onClick={onClose} className="p-2" data-testid="hr-emp-form-close">
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-canvas-line px-6">
          {[
            { k: "basic", label: "Basic" },
            { k: "employment", label: "Employment" },
            // Salary / bank / payroll config tab is hidden for roles
            // without hr.salary_view (backend scrubs + blocks as well).
            ...(canViewSalary ? [{ k: "bank", label: "Bank + Payroll" }] : []),
            { k: "kyc", label: "KYC + Emergency" },
            ...(editing ? [{ k: "docs", label: `Documents (${docs.length})` }] : []),
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setTab(t.k)}
              data-testid={`hr-emp-tab-${t.k}`}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-wider border-b-2 -mb-px ${
                tab === t.k ? "border-ink text-ink" : "border-transparent text-ink-muted hover:text-ink"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          {tab === "basic" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Employee Code *" testId="hr-code">
                <input value={form.employee_code} onChange={(e) => set("employee_code", e.target.value)} data-testid="hr-emp-code" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
              </Field>
              <Field label="Full Name *" testId="hr-name">
                <input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="hr-emp-name" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
              </Field>
              <Field label="Designation">
                <input value={form.designation} onChange={(e) => set("designation", e.target.value)} data-testid="hr-emp-designation" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" placeholder="MANAGER / EXECUTIVE / TECHNICIAN" />
              </Field>
              <Field label="Work Area (Department)">
                <input value={form.work_area} onChange={(e) => set("work_area", e.target.value)} data-testid="hr-emp-work-area" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" placeholder="SALES / SERVICE / ACCOUNTS" />
              </Field>
              <Field label="Phone">
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="hr-emp-phone" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
              </Field>
              <Field label="Email">
                <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="hr-emp-email" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
              </Field>
              <Field label="Address" testId="hr-address">
                <textarea rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="hr-emp-address" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
              </Field>
              <Field label="Notes">
                <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="hr-emp-notes" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
              </Field>
            </div>
          )}

          {tab === "employment" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Branch *">
                <select value={form.branch_id || ""} onChange={(e) => set("branch_id", e.target.value || null)} data-testid="hr-emp-branch" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
                  <option value="">— Unassigned —</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="hr-emp-status" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
                  <option value="active">Active</option>
                  <option value="on_leave">On Leave</option>
                  <option value="resigned">Resigned</option>
                  <option value="terminated">Terminated</option>
                </select>
              </Field>
              <Field label="Date of Joining">
                <input type="date" value={form.date_of_joining || ""} onChange={(e) => set("date_of_joining", e.target.value)} data-testid="hr-emp-doj" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
              </Field>
              <Field label="Date of Leaving">
                <input type="date" value={form.date_of_leaving || ""} onChange={(e) => set("date_of_leaving", e.target.value)} data-testid="hr-emp-dol" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
              </Field>
              <div className="col-span-2 border-t border-canvas-line pt-4">
                <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono mb-2">Shift (optional)</div>
                <div className="text-[11px] text-ink-muted mb-3">
                  Set start &amp; end for shift-based teams (e.g. Service 09:00–18:30). Leave blank for staff with no fixed hours (e.g. Sales) — they'll get simple P/A marking without time capture.
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Shift Start (HH:MM)">
                    <input
                      type="time"
                      value={form.shift_start || ""}
                      onChange={(e) => set("shift_start", e.target.value || null)}
                      data-testid="hr-emp-shift-start"
                      className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
                    />
                  </Field>
                  <Field label="Shift End (HH:MM)">
                    <input
                      type="time"
                      value={form.shift_end || ""}
                      onChange={(e) => set("shift_end", e.target.value || null)}
                      data-testid="hr-emp-shift-end"
                      className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
                    />
                  </Field>
                </div>
              </div>
            </div>
          )}

          {tab === "bank" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Bank Name">
                  <input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} data-testid="hr-emp-bank" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
                </Field>
                <Field label="Bank A/C Number">
                  <input value={form.bank_account} onChange={(e) => set("bank_account", e.target.value)} data-testid="hr-emp-ac" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
                </Field>
                <Field label="IFSC Code">
                  <input value={form.ifsc_code} onChange={(e) => set("ifsc_code", e.target.value.toUpperCase())} data-testid="hr-emp-ifsc" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
                </Field>
                <Field label="Mode of Pay">
                  <select value={form.mode_of_pay} onChange={(e) => set("mode_of_pay", e.target.value)} data-testid="hr-emp-mode" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
                    <option value="bank">Bank</option>
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </Field>
                <Field label="Paying Source A/C">
                  <select value={form.source_paying_account} onChange={(e) => set("source_paying_account", e.target.value)} data-testid="hr-emp-src-ac" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
                    <option value="228">228 A/c</option>
                    <option value="3962">3962 A/c</option>
                    <option value="cash">Cash</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
              </div>
              <div className="border-t border-canvas-line pt-4">
                <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono mb-3">Payroll Config</div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Gross Salary (₹ / month)">
                    <input type="number" step="0.01" value={form.gross_salary} onChange={(e) => set("gross_salary", parseFloat(e.target.value) || 0)} data-testid="hr-emp-gross" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
                  </Field>
                  <Field label="Salary Credit Day">
                    <input type="number" min="1" max="31" value={form.salary_credit_day || ""} onChange={(e) => set("salary_credit_day", parseInt(e.target.value) || null)} data-testid="hr-emp-credit-day" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
                  </Field>
                  <Field label="ESI/PF Filing Branch">
                    <input value={form.esi_pf_branch} onChange={(e) => set("esi_pf_branch", e.target.value)} data-testid="hr-emp-esi-branch" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
                  </Field>
                  <div />
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!form.pf_applicable} onChange={(e) => set("pf_applicable", e.target.checked)} data-testid="hr-emp-pf" />
                    <span>PF applicable</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!form.esi_applicable} onChange={(e) => set("esi_applicable", e.target.checked)} data-testid="hr-emp-esi" />
                    <span>ESI applicable</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!form.holiday_pay_bonus} onChange={(e) => set("holiday_pay_bonus", e.target.checked)} data-testid="hr-emp-hpb" />
                    <span>Holiday Pay / Bonus eligible</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!form.has_salary_advance} onChange={(e) => set("has_salary_advance", e.target.checked)} data-testid="hr-emp-sa" />
                    <span>Has running salary advance</span>
                  </label>
                </div>
                {/* Payroll working-schedule + advance recovery — used by the
                    Payroll Preview to derive per-day rate and month-end
                    deductions. Defaults 25 days / 200 hours (Sales); set
                    26 / 208 for workshop/service employees. */}
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <Field label="Working days / month">
                    <input type="number" min={20} max={31} value={form.working_days_per_month ?? 25} onChange={(e) => set("working_days_per_month", parseInt(e.target.value, 10) || 25)} data-testid="hr-emp-wd" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
                  </Field>
                  <Field label="Working hours / month">
                    <input type="number" min={160} max={260} value={form.working_hours_per_month ?? 200} onChange={(e) => set("working_hours_per_month", parseInt(e.target.value, 10) || 200)} data-testid="hr-emp-wh" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
                  </Field>
                </div>

                {/* Salary Advance — installment schedule. Shown only when
                    the "Has running salary advance" toggle is on. */}
                {form.has_salary_advance && (
                  <div className="mt-3 p-3 border border-canvas-line bg-canvas-panel/40 space-y-3" data-testid="hr-emp-advance-block">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Salary Advance Schedule</div>
                    <div className="grid grid-cols-3 gap-3">
                      <Field label="Total advance ₹">
                        <input
                          type="number" step="1" min={0}
                          value={form.advance_total_amount ?? 0}
                          onChange={(e) => set("advance_total_amount", parseFloat(e.target.value) || 0)}
                          data-testid="hr-emp-adv-total"
                          className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
                        />
                      </Field>
                      <Field label="Repayment months">
                        <input
                          type="number" step="1" min={0} max={60}
                          value={form.advance_installments ?? 0}
                          onChange={(e) => set("advance_installments", parseInt(e.target.value, 10) || 0)}
                          data-testid="hr-emp-adv-months"
                          className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
                        />
                      </Field>
                      <Field label="Start month (YYYY-MM)">
                        <input
                          type="month"
                          value={form.advance_start_month || ""}
                          onChange={(e) => set("advance_start_month", e.target.value)}
                          data-testid="hr-emp-adv-start"
                          className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
                        />
                      </Field>
                    </div>
                    {(() => {
                      const tot = Number(form.advance_total_amount) || 0;
                      const inst = Number(form.advance_installments) || 0;
                      if (tot > 0 && inst > 0) {
                        const emi = Math.round((tot / inst) * 100) / 100;
                        return (
                          <div className="grid grid-cols-2 gap-3 text-xs">
                            <div className="px-3 py-2 border border-ink/40 bg-canvas">
                              <div className="font-mono text-[9px] uppercase text-ink-muted">Monthly EMI (auto)</div>
                              <div className="font-mono tabular-nums text-sm font-bold mt-0.5" data-testid="hr-emp-adv-emi-preview">₹{emi.toLocaleString("en-IN")}</div>
                            </div>
                            <div className="px-3 py-2 border border-canvas-line">
                              <div className="font-mono text-[9px] uppercase text-ink-muted">Recovers by</div>
                              <div className="font-mono text-[11px] mt-0.5">
                                {form.advance_start_month
                                  ? (() => {
                                      const [y, m] = form.advance_start_month.split("-").map(Number);
                                      const endMonth = m + inst - 1;
                                      const endY = y + Math.floor((endMonth - 1) / 12);
                                      const endM = ((endMonth - 1) % 12) + 1;
                                      return `${String(endM).padStart(2, "0")}/${endY}`;
                                    })()
                                  : "— pick a start month —"}
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <Field label="Manual recovery / month (₹) — legacy mode">
                          <input
                            type="number" step="1" min={0}
                            value={form.advance_recovery_amount ?? 0}
                            onChange={(e) => set("advance_recovery_amount", parseFloat(e.target.value) || 0)}
                            data-testid="hr-emp-adv-recovery"
                            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
                          />
                        </Field>
                      );
                    })()}
                    <div className="font-mono text-[10px] text-ink-subtle">
                      Fill Total + Repayment months to auto-derive the EMI; deduction stops when the balance hits zero.
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "kyc" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Aadhar No">
                <input value={form.aadhar_no} onChange={(e) => set("aadhar_no", e.target.value)} data-testid="hr-emp-aadhar" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
              </Field>
              <Field label="PAN No">
                <input value={form.pan_no} onChange={(e) => set("pan_no", e.target.value.toUpperCase())} data-testid="hr-emp-pan" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
              </Field>
              <Field label="Emergency Contact Name">
                <input value={form.emergency_contact_name} onChange={(e) => set("emergency_contact_name", e.target.value)} data-testid="hr-emp-emg-name" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
              </Field>
              <Field label="Emergency Contact Phone">
                <input value={form.emergency_contact_phone} onChange={(e) => set("emergency_contact_phone", e.target.value)} data-testid="hr-emp-emg-phone" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
              </Field>
              <Field label="Relationship">
                <input value={form.emergency_contact_relation} onChange={(e) => set("emergency_contact_relation", e.target.value)} data-testid="hr-emp-emg-rel" className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" placeholder="Father / Spouse / …" />
              </Field>
            </div>
          )}

          {tab === "docs" && editing && (
            <DocsTab employeeId={editing.id} docs={docs} onChanged={refetch} />
          )}

          {tab !== "docs" && (
            <div className="pt-4 border-t border-canvas-line flex items-center justify-end gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-canvas-line text-sm" data-testid="hr-emp-cancel">Cancel</button>
              <button type="submit" disabled={saving} data-testid="hr-emp-save" className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-50">
                {saving ? "Saving…" : editing ? "Update Employee" : "Create Employee"}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
